import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, Button, Space, message, Dropdown, Menu, Modal, Input, Row, Col } from 'antd';
import { PlusOutlined, UserAddOutlined, DeleteOutlined, EditOutlined, EyeOutlined, MoreOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { SpaceUserGroup, SpaceUserGroupMember } from '../../../../../types/spaceUserGroups';
import { setSpaceUserGroups, setLoading, setError } from '../../../../../reducers/reducerSpaceUserGroups';
import CreateGroupModal from './CreateGroupModal';
import ManageGroupMembersModal from './components/ManageGroupMembersModal';
import { SpaceUserGroupService } from '../../../../../services/SpaceUserGroupService';
import moment from 'moment';

interface RouteParams {
  id: string;
}

const SpaceUserGroups: React.FC = () => {
  const dispatch = useDispatch();
  const { id: spaceId } = useParams<RouteParams>();
  const { groups, loading } = useSelector((state: any) => state.spaceUserGroups);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<SpaceUserGroup | null>(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [searchText, setSearchText] = useState('');

  const fetchGroups = async () => {
    try {
      dispatch(setLoading(true));
      const service = new SpaceUserGroupService(`${spaceId}/user/groups/list`);
      const groups = await service.listGroups();
      dispatch(setSpaceUserGroups(groups));
    } catch (error: any) {
      const errorMessage = error?.message || 'An error occurred';
      dispatch(setError(errorMessage));
      message.error(errorMessage);
    } finally {
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    fetchGroups();
  }, [dispatch, spaceId]);

  const handleCreateGroup = async (values: { name: string }) => {
    try {
      dispatch(setLoading(true));
      const service = new SpaceUserGroupService(`${spaceId}/user/group/create`);
      await service.createGroup(values);
      const listService = new SpaceUserGroupService(`${spaceId}/user/groups/list`);
      const groups = await listService.listGroups();
      dispatch(setSpaceUserGroups(groups));
      setShowCreateModal(false);
      message.success('Group created successfully');
      return true
    } catch (error: any) {
      const errorMessage = error?.message || 'An error occurred';
      dispatch(setError(errorMessage));
      message.error(errorMessage);
      return false
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleDeleteGroup = async (groupId: string) => {
    try {
      dispatch(setLoading(true));
      const service = new SpaceUserGroupService(`${spaceId}/user/group/${groupId}`);
      await service.deleteGroup();
      const listService = new SpaceUserGroupService(`${spaceId}/user/groups/list`);
      const groups = await listService.listGroups();
      dispatch(setSpaceUserGroups(groups));
      message.success('Group deleted successfully');
    } catch (error: any) {
      const errorMessage = error?.message || 'An error occurred';
      dispatch(setError(errorMessage));
      message.error(errorMessage);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleRenameGroup = async () => {
    try {
      dispatch(setLoading(true));
      const service = new SpaceUserGroupService(`${spaceId}/user/group/${selectedGroup?.id}`);
      await service.renameGroup(selectedGroup!.id, { name: newGroupName });
      const listService = new SpaceUserGroupService(`${spaceId}/user/groups/list`);
      const groups = await listService.listGroups();
      dispatch(setSpaceUserGroups(groups));
      setShowRenameModal(false);
      setSelectedGroup(null);
      setNewGroupName('');
      message.success('Group renamed successfully');
    } catch (error: any) {
      const errorMessage = error?.message || 'An error occurred';
      dispatch(setError(errorMessage));
      message.error(errorMessage);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const getFilteredGroups = () => {
    if (!searchText.trim()) {
      return groups || [];
    }
    return (groups || []).filter((group: SpaceUserGroup) => 
      group.name.toLowerCase().includes(searchText.toLowerCase())
    );
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: '40%',
    },
    {
      title: 'Members',
      dataIndex: 'members',
      key: 'members',
      render: (members: SpaceUserGroupMember[]) => members?.length || 0,
      width: '15%',
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: '30%',
      render: (date: string) => moment(date).format('MMM D, YYYY, h:mm A'),
      sorter: (a: SpaceUserGroup, b: SpaceUserGroup) => moment(a.created_at).unix() - moment(b.created_at).unix(),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '15%',
      align: 'right' as const,
      render: (record: SpaceUserGroup) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'manage',
                icon: <UserAddOutlined />,
                label: 'Manage Members',
                onClick: () => {
                  setSelectedGroup(record);
                  setShowViewModal(true);
                }
              },
              {
                type: 'divider'
              },
              {
                key: 'rename',
                icon: <EditOutlined />,
                label: 'Rename',
                onClick: () => {
                    setSelectedGroup(record);
                    setNewGroupName(record.name);
                    setShowRenameModal(true);
                }
              },
              {
                type: 'divider'
              },
              {
                key: 'delete',
                icon: <DeleteOutlined />,
                label: 'Delete',
                danger: true,
                onClick: () => {
                  Modal.confirm({
                    title: 'Are you sure you want to delete this group?',
                    okText: 'Yes',
                    cancelText: 'No',
                    centered: true,
                    maskClosable: true,
                    onOk: () => handleDeleteGroup(record.id),
                  });
                }
              }
            ]
          }}
          placement="bottomRight"
          trigger={['click']}
        >
          <Button 
            type="text" 
            icon={<MoreOutlined />}
            style={{ marginRight: -8 }}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="space-user-groups">
      <Row gutter={20} align="middle" style={{ marginBottom: 16 }}>
        <Col xs={24} md={12} lg={8}>
          <Input.Search
            placeholder="Search groups by name"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            style={{ width: '100%' }}
          />
        </Col>
        <Col xs={24} md={12} lg={16} style={{ textAlign: 'right' }}>
        <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setShowCreateModal(true)}
          >
            Create Group
          </Button>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={getFilteredGroups()}
        loading={loading}
        rowKey="id"
        pagination={{
          defaultPageSize: 10,
          hideOnSinglePage: true,
        }}
        size="middle"
        bordered={false}
        scroll={{ x: 800 }}
      />

      <CreateGroupModal
        visible={showCreateModal}
        onCancel={() => setShowCreateModal(false)}
        onSubmit={handleCreateGroup}
        loading={loading}
      />

      <Modal
        title="Rename Group"
        visible={showRenameModal}
        centered
        maskClosable
        onCancel={() => {
          setShowRenameModal(false);
          setSelectedGroup(null);
          setNewGroupName('');
        }}
        onOk={handleRenameGroup}
        confirmLoading={loading}
      >
        <Input
          placeholder="Enter new group name"
          value={newGroupName}
          onChange={(e) => setNewGroupName(e.target.value)}
          autoFocus
        />
      </Modal>

      {selectedGroup && (
        <ManageGroupMembersModal
          visible={showViewModal}
          onCancel={() => {
            setShowViewModal(false);
            setSelectedGroup(null);
          }}
          loading={loading}
          group={selectedGroup}
          spaceId={spaceId}
          onMemberRemoved={async (memberId: string) => {
            try {
              dispatch(setLoading(true));
              const removeMemberService = new SpaceUserGroupService(`${spaceId}/user/group/${selectedGroup.id}/member/${memberId}`);
              await removeMemberService.removeMember();
              message.success('Member removed successfully');
              const listService = new SpaceUserGroupService(`${spaceId}/user/groups/list`);
              const groups = await listService.listGroups();
              dispatch(setSpaceUserGroups(groups));
              const updatedGroup = groups.find((group: SpaceUserGroup) => group.id === selectedGroup.id) || null;
              setSelectedGroup(updatedGroup);
            } catch (error: any) {
              const errorMessage = error?.message || 'An error occurred';
              dispatch(setError(errorMessage));
              message.error(errorMessage);
            } finally {
              dispatch(setLoading(false));
            }
          }}
          onMembersAssigned={async (memberIds) => {
            try {
              dispatch(setLoading(true));
              const service = new SpaceUserGroupService(`${spaceId}/user/group/${selectedGroup.id}/members/assign`);
              await service.assignMembers(selectedGroup.id, { members: memberIds });
              const listService = new SpaceUserGroupService(`${spaceId}/user/groups/list`);
              const groups = await listService.listGroups();
              dispatch(setSpaceUserGroups(groups));
              const updatedGroup = groups.find((group: SpaceUserGroup) => group.id === selectedGroup.id) || null;
              setSelectedGroup(updatedGroup);
              message.success('Members assigned successfully');
            } catch (error: any) {
              const errorMessage = error?.message || 'An error occurred';
              dispatch(setError(errorMessage));
              message.error(errorMessage);
            } finally {
              dispatch(setLoading(false));
            }
          }}
        />
      )}
    </div>
  );
};

export default SpaceUserGroups; 